/**
 * SSOrgUnits — Organizational Unit Management
 * ✅ Refactored to use shared SS component library (components/ss).
 * API: ssOrgUnitAPI, ssCompanyAPI, ssUserAPI from ssApi.js
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ssOrgUnitAPI, ssCompanyAPI, ssUserAPI } from '../../services/ssApi';
import {
  Page, PageHeader, Card, Modal, SplitLayout, TabBar, Section,
  Button, Badge, Field, Input, Select,
  Spinner, EmptyState, ErrorBanner,
  DataTable, InfoGrid,
  ActiveBadge,
  COLOR, FONT,
} from '../../components/ss';

// ── Build tree from flat list ─────────────────────────────────────────────────
// Backend list serializer returns { id, name, code, companyid, parent }.
const unitId     = u => u?.id ?? u?.orgunitid;
const unitParent = u => u?.parent ?? u?.parentorganizationunitid ?? u?.parentorgunitid;

function buildTree(flat) {
  const byId = {};
  flat.forEach(u => { byId[unitId(u)] = { ...u, children: [] }; });
  const roots = [];
  flat.forEach(u => {
    const pid = unitParent(u);
    if (pid && byId[pid]) {
      byId[pid].children.push(byId[unitId(u)]);
    } else {
      roots.push(byId[unitId(u)]);
    }
  });
  return roots;
}

// ── Org Unit Tree node (domain-specific — kept, uses library components) ──────
function OrgTreeNode({ node, depth = 0, selected, onSelect }) {
  const [open, setOpen] = useState(depth === 0);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected  = selected != null && unitId(selected) === unitId(node);

  return (
    <div>
      <div
        onClick={() => { onSelect(node); if (hasChildren) setOpen(o => !o); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '7px 10px',
          paddingLeft: 10 + depth * 20,
          borderRadius: 7,
          cursor: 'pointer',
          background: isSelected ? COLOR.blueBg : 'transparent',
          border: `1px solid ${isSelected ? COLOR.blueBorder : 'transparent'}`,
          marginBottom: 2,
          fontSize: FONT.size.base,
          fontWeight: isSelected ? FONT.weight.semi : FONT.weight.normal,
          color: COLOR.text,
        }}
      >
        <span style={{ fontSize: 10, color: COLOR.grayLight, width: 12, textAlign: 'center', flexShrink: 0 }}>
          {hasChildren ? (open ? '▾' : '▸') : ''}
        </span>
        <span style={{ fontSize: 16, flexShrink: 0 }}>🗂️</span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {node.name || node.orgunitname || `Unit #${unitId(node)}`}
        </span>
        {node.isactive === false && <Badge color="amber" style={{ marginLeft: 4 }}>Inactive</Badge>}
      </div>
      {hasChildren && open && node.children.map(child => (
        <OrgTreeNode key={unitId(child)} node={child} depth={depth + 1} selected={selected} onSelect={onSelect} />
      ))}
    </div>
  );
}

// ── Create / Edit form ────────────────────────────────────────────────────────
function OrgUnitForm({ open, unit, companies, allUnits, companyId, onClose, onDone }) {
  const isEdit = !!unit;
  const [form, setForm] = useState({ name: '', code: '', companyid: companyId || '', parentorgunitid: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (unit) {
      setForm({ name: unit.name || unit.orgunitname || '', code: unit.code || '', companyid: unit.companyid || companyId || '', parentorgunitid: unitParent(unit) || '' });
    } else {
      setForm({ name: '', code: '', companyid: companyId || '', parentorgunitid: '' });
    }
    setErr('');
  }, [unit, companyId, open]);

  const upd = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!form.name) { setErr('Name is required.'); return; }
    setSaving(true); setErr('');
    try {
      // Backend OrgUnitBody: { companyid, parentorganizationunitid, name, code }
      const payload = {
        name: form.name,
        code: form.code || null,
        companyid: form.companyid ? Number(form.companyid) : null,
        parentorganizationunitid: form.parentorgunitid ? Number(form.parentorgunitid) : null,
      };
      if (isEdit) await ssOrgUnitAPI.update(unitId(unit), payload);
      else        await ssOrgUnitAPI.create(payload);
      onDone();
    } catch (ex) { setErr(ex.response?.data?.detail || ex.message || 'Save failed'); }
    finally { setSaving(false); }
  }

  return (
    <Modal open={open} title={isEdit ? `Edit — ${unit?.name || unit?.orgunitname}` : 'New Org Unit'} onClose={onClose} fullscreen={!isEdit}>
      <div style={!isEdit ? { maxWidth: 860, margin: '0 auto' } : {}}>
      <ErrorBanner>{err}</ErrorBanner>
      <form onSubmit={submit}>
        <div style={{ display: 'grid', gridTemplateColumns: isEdit ? '1fr' : 'repeat(3, 1fr)', gap: 14, marginBottom: 18 }}>
          <Field label="Company" required style={{ marginBottom: 0 }}>
            <Select value={form.companyid} onChange={upd('companyid')}>
              <option value="">— select company —</option>
              {companies.map(c => <option key={c.companyid} value={c.companyid}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Name" required style={{ marginBottom: 0 }}>
            <Input value={form.name} onChange={upd('name')} placeholder="e.g. East Region" />
          </Field>
          <Field label="Code" style={{ marginBottom: 0 }}>
            <Input value={form.code} onChange={upd('code')} placeholder="e.g. EAST_REG" />
          </Field>
          <Field label="Parent Org Unit" style={{ marginBottom: 0 }}>
            <Select value={form.parentorgunitid} onChange={upd('parentorgunitid')}>
              <option value="">— top level —</option>
              {allUnits.filter(u => !unit || unitId(u) !== unitId(unit)).map(u => (
                <option key={unitId(u)} value={unitId(u)}>{u.name || u.orgunitname}</option>
              ))}
            </Select>
          </Field>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Org Unit'}
          </Button>
        </div>
      </form>
      </div>
    </Modal>
  );
}

// ── Detail panel ──────────────────────────────────────────────────────────────
function OrgUnitDetail({ unit, allUsers, onEdit, onDeactivate, onClose }) {
  const [unitUsers, setUnitUsers]     = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [assignUserId, setAssignUserId] = useState('');
  const [assigning, setAssigning]     = useState(false);
  const [err, setErr]                 = useState('');
  const [tab, setTab]                 = useState('info');

  const loadUsers = useCallback(() => {
    if (!unit) return;
    setLoadingUsers(true);
    ssOrgUnitAPI.listUsers(unitId(unit))
      .then(d => setUnitUsers(d.data || d || []))
      .catch(() => {})
      .finally(() => setLoadingUsers(false));
  }, [unit]);

  useEffect(() => { setTab('info'); setErr(''); if (unit) loadUsers(); else setUnitUsers([]); }, [unit, loadUsers]);

  if (!unit) return (
    <EmptyState icon="🗂️" title="Select an org unit" message="Click a unit in the tree on the left." />
  );

  async function assignUser() {
    if (!assignUserId) return;
    setAssigning(true); setErr('');
    try { await ssOrgUnitAPI.assignUser(unitId(unit), assignUserId); setAssignUserId(''); loadUsers(); }
    catch (ex) { setErr(ex.response?.data?.detail || ex.message || 'Assign failed'); }
    finally { setAssigning(false); }
  }

  async function removeUser(userId) {
    try { await ssOrgUnitAPI.removeUser(unitId(unit), userId); loadUsers(); }
    catch (ex) { setErr(ex.response?.data?.detail || ex.message); }
  }

  const USER_COLS = [
    { key: 'username', label: 'Username', render: v => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'email',    label: 'Email' },
    { key: '_rm', label: '', render: (_, u) => <Button variant="danger" size="sm" onClick={() => removeUser(u.userid)}>Remove</Button> },
  ];

  const TABS = [
    { id: 'info',  label: 'Info' },
    { id: 'users', label: `Users (${unitUsers.length})` },
  ];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: FONT.weight.bold, color: COLOR.text }}>
            🗂️ {unit.name || unit.orgunitname}
          </div>
          <div style={{ fontSize: 12, color: COLOR.textMuted, marginTop: 2 }}>
            ID #{unitId(unit)}{unit.code ? ` · ${unit.code}` : ''}
          </div>
          <div style={{ marginTop: 8 }}><ActiveBadge active={unit.isactive !== false} /></div>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Button variant="ghost" onClick={() => onEdit(unit)}>Edit</Button>
          {unit.isactive !== false && <Button variant="danger" onClick={() => onDeactivate(unit)}>Deactivate</Button>}
          <Button variant="ghost" onClick={onClose}>×</Button>
        </div>
      </div>

      <TabBar tabs={TABS} active={tab} onChange={setTab} style={{ marginBottom: 16 }} />
      <ErrorBanner>{err}</ErrorBanner>

      {tab === 'info' && (
        <InfoGrid rows={[
          ['Org Unit ID',  unitId(unit)],
          ['Name',         unit.name || unit.orgunitname],
          ['Code',         unit.code],
          ['Company ID',   unit.companyid],
          ['Parent Unit',  unitParent(unit) ? `#${unitParent(unit)}` : 'Top level'],
          ['Status',       unit.isactive === false ? 'Inactive' : 'Active'],
        ]} />
      )}

      {tab === 'users' && (
        loadingUsers ? <Spinner /> : (
          <>
            <DataTable
              columns={USER_COLS}
              rows={unitUsers}
              rowKey="userid"
              emptyIcon="👤"
              emptyTitle="No users assigned"
              style={{ marginBottom: 16 }}
            />
            <Section title="Assign User">
              <div style={{ display: 'flex', gap: 8 }}>
                <Select
                  style={{ flex: 1 }}
                  value={assignUserId}
                  onChange={e => setAssignUserId(e.target.value)}
                >
                  <option value="">— select user —</option>
                  {allUsers
                    .filter(u => !unitUsers.find(uu => uu.userid === u.userid))
                    .map(u => <option key={u.userid} value={u.userid}>{u.username}</option>)
                  }
                </Select>
                <Button variant="success" onClick={assignUser} disabled={assigning || !assignUserId}>
                  {assigning ? '…' : 'Assign'}
                </Button>
              </div>
            </Section>
          </>
        )
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function SSOrgUnits() {
  const [companies, setCompanies] = useState([]);
  const [allUnits, setAllUnits]   = useState([]);
  const [tree, setTree]           = useState([]);
  const [allUsers, setAllUsers]   = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [loading, setLoading]     = useState(false);
  const [err, setErr]             = useState('');
  const [selected, setSelected]   = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editUnit, setEditUnit]   = useState(null);

  useEffect(() => {
    ssCompanyAPI.list()
      .then(d => {
        const list = d.data || d || [];
        setCompanies(list);
        if (list.length > 0 && !companyId) setCompanyId(String(list[0].companyid));
      }).catch(() => {});
    ssUserAPI.list().then(d => setAllUsers(d.data || d || [])).catch(() => {});
  }, []); // eslint-disable-line

  const loadUnits = useCallback(() => {
    if (!companyId) return;
    setLoading(true); setErr('');
    ssOrgUnitAPI.listForCompany(companyId)
      .then(d => { const flat = d.data || d || []; setAllUnits(flat); setTree(buildTree(flat)); })
      .catch(ex => setErr(ex.response?.data?.detail || ex.message || 'Failed to load org units'))
      .finally(() => setLoading(false));
  }, [companyId]);

  useEffect(() => { loadUnits(); setSelected(null); }, [loadUnits]);

  async function handleDeactivate(unit) {
    if (!window.confirm(`Deactivate org unit "${unit.name || unit.orgunitname}"?`)) return;
    try { await ssOrgUnitAPI.deactivate(unitId(unit)); loadUnits(); setSelected(null); }
    catch (ex) { setErr(ex.response?.data?.detail || ex.message || 'Deactivate failed'); }
  }

  return (
    <Page>
      <PageHeader
        title="Org Units"
        subtitle="Manage the organizational hierarchy within companies — ssorganizationunits"
        action={<Button onClick={() => setShowCreate(true)}>+ New Org Unit</Button>}
      />

      <ErrorBanner>{err}</ErrorBanner>

      {/* Company selector */}
      <Card style={{ marginBottom: 18, padding: '12px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: FONT.size.sm, fontWeight: FONT.weight.semi, color: COLOR.textSub, whiteSpace: 'nowrap' }}>Company:</label>
          <Select style={{ maxWidth: 280 }} value={companyId} onChange={e => setCompanyId(e.target.value)}>
            <option value="">— select company —</option>
            {companies.map(c => <option key={c.companyid} value={c.companyid}>{c.name}</option>)}
          </Select>
          <span style={{ fontSize: 12, color: COLOR.textMuted }}>{allUnits.length} unit(s)</span>
        </div>
      </Card>

      {/* Split: tree + detail */}
      <SplitLayout
        listWidth={300}
        list={
          <Card style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
            {loading ? <Spinner /> : tree.length === 0 ? (
              <EmptyState icon="🗂️" title={companyId ? 'No org units' : 'Select a company'} />
            ) : tree.map(root => (
              <OrgTreeNode key={unitId(root)} node={root} selected={selected} onSelect={setSelected} />
            ))}
          </Card>
        }
        detail={
          <Card style={{ minHeight: 400 }}>
            <OrgUnitDetail
              unit={selected}
              allUsers={allUsers}
              onEdit={unit => setEditUnit(unit)}
              onDeactivate={handleDeactivate}
              onClose={() => setSelected(null)}
            />
          </Card>
        }
      />

      <OrgUnitForm open={showCreate}   unit={null}     companies={companies} allUnits={allUnits} companyId={companyId} onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); loadUnits(); }} />
      <OrgUnitForm open={!!editUnit}   unit={editUnit}  companies={companies} allUnits={allUnits} companyId={companyId} onClose={() => setEditUnit(null)}   onDone={() => { setEditUnit(null);   loadUnits(); }} />
    </Page>
  );
}
